import { 
    Dropdown, 
    Option, 
    Button, 
    Label, 
    makeStyles, 
    shorthands, 
    tokens, 
    OverlayDrawer, 
    DrawerHeader, 
    DrawerHeaderTitle, 
    DrawerBody,
    Input,
    Tooltip,
    MessageBar,
    MessageBarBody,
    Checkbox
} from "@fluentui/react-components";
import { 
    FilterRegular, 
    DismissRegular, 
    ArrowCounterclockwiseRegular,
    SearchRegular
} from "@fluentui/react-icons";
import { getOptionsFromDimension, getKeyFriendlyName } from "../../pages/dashboard/DocumentSearchFilterOptions";
import { FilterKey } from "../../pages/dashboard/DocumentSearchFilters";
import { ChartTitleFriendlyNames } from "../../utils/FriendlyNameMappings";
import React, { useState } from 'react';
import { FilterPanelProps } from "./FilterPanelProps";
import { getDefaultSearchParams } from "../../controller/dashboardController";
import Configuration from "../../config/einvoicing-configuration";
import "./FilterPanel.css";

// Define local interface for Dropdown options to replace v8 IDropdownOption

interface IChartDataPoint {
    legend: string;
    data: number;
    color: string;
    key: string;
    onClick?: () => void;
    chartTitle?: string;
}

interface IChartProps {
    chartTitle: string;
    chartData: IChartDataPoint[];
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    searchContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
        flexWrap: 'wrap',
    },
    searchBox: {
        minWidth: '18.75rem',
        flexGrow: 1,
    },
    filterButtonContainer: {
        display: 'flex',
        alignItems: 'center',
        marginTop: '0.5rem',
        marginBottom: '0.25rem',
    },
    filterPillsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap('0.25rem'),
        alignItems: 'center',
        marginLeft: '0.75rem',
    },
    pill: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground1,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.padding('0.25rem', '0.5rem'),
        fontSize: tokens.fontSizeBase200,
        minHeight: '1.5rem',
    },
    pillButton: {
        marginLeft: '0.25rem',
        minWidth: '1.5rem',
        height: '1.5rem',
        minHeight: '1.5rem',
        ...shorthands.padding('0'),
    },
    drawer: {
        width: '37.5rem',
        maxWidth: '100vw',
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalL),
    },
    dateTimeContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    dateTimeRow: {
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.gap(tokens.spacingHorizontalM),
        alignItems: 'flex-end',
    },
    datePicker: {
        flexGrow: 2,
        minWidth: '12.5rem',
        display: 'flex',
        flexDirection: 'column',
    },
    timeInput: {
        flexGrow: 1,
        minWidth: '7.5rem',
        display: 'flex',
        flexDirection: 'column',
    },
    dropdown: {
        width: '100%',
        marginBottom: '0.625rem',
        display: 'flex',
        flexDirection: 'column',
    },
    chartContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalS),
        marginTop: tokens.spacingVerticalL,
        width: '100%',
    },
    chartRow: {
        display: 'flex',
        flexDirection: 'row',
        overflowX: 'auto',
        ...shorthands.gap(tokens.spacingHorizontalL),
        width: '100%',
    },
    chartItem: {
        minWidth: '18.75rem',
        flexGrow: 1,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.padding('4px'),
        ':focus-visible': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: tokens.colorBrandStroke1,
            outlineOffset: '-2px',
        },
    },
    inputControl: {
        width: '100%',
    },
    card: {
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
        boxShadow: tokens.shadow4,
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    drawerBody: {
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.padding(tokens.spacingVerticalM),
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalL),
    },
    tooltipContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap('0.25rem'),
        padding: '0.25rem',
    },
    tooltipValue: {
        fontSize: '1.125rem',
        fontWeight: 'bold',
    },
    tooltipLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    tooltipSubtext: {
        fontSize: '0.625rem',
        color: tokens.colorNeutralForeground1,
    },
    chartLegendLabel: {
        color: tokens.colorNeutralForeground1,
    }
});

const SimpleStackedBarChart = ({ data }: { data: IChartProps }) => {
const styles = useStyles();
const visibleChartData = data.chartData.filter((point: IChartDataPoint) => point.data > 0);
const total = visibleChartData.reduce((acc: number, curr: IChartDataPoint) => acc + curr.data, 0);
if (total === 0) return null;

return (
    <div style={{ width: '100%' }}>
        <h3 style={{ marginBottom: '0.25rem', fontWeight: 600, margin: '0 0 0.25rem 0', fontSize: '0.875rem', color: '#242424' }}>{data.chartTitle}</h3>
        <div style={{ display: 'flex', height: '1.25rem', width: '100%', borderRadius: '0.25rem', overflow: 'hidden' }}>
            {visibleChartData.map((point: IChartDataPoint, index: number) => (
                    <Tooltip
                        key={index}
                        content={
                            <div className={styles.tooltipContainer}>
                                <span className={styles.tooltipLabel}>{point.legend}</span>
                                <span className={styles.tooltipValue}>{point.data}</span>
                                <span className={styles.tooltipSubtext}>{data.chartTitle}</span>
                            </div>
                        }
                        relationship="description" >
                        {point.onClick ? (
                            <div
                                role="button"
                                tabIndex={0}
                                aria-label={`Filter by ${point.legend}: ${point.data}`}
                                style={{
                                    width: `${(point.data / total) * 100}%`,
                                    backgroundColor: point.color,
                                    height: '100%',
                                    minHeight: '1.5rem',
                                    minWidth: '1.5rem',
                                    cursor: 'pointer',
                                }}
                                onClick={point.onClick}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        point.onClick?.();
                                    }
                                }}
                            />
                        ) : (
                            <div
                                role="img"
                                aria-label={`${point.legend}: ${point.data}`}
                                style={{
                                    width: `${(point.data / total) * 100}%`,
                                    backgroundColor: point.color,
                                    height: '100%',
                                    cursor: 'default',
                                }}
                            />
                        )}
                    </Tooltip>
                ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
                {data.chartData.map((point: IChartDataPoint, index: number) => {
                    const legendStyle: React.CSSProperties = {
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.75rem',
                        color: '#242424',
                        cursor: point.onClick ? 'pointer' : 'default',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        transition: 'background-color 0.2s',
                        minHeight: '1.5rem',
                        minWidth: '1.5rem',
                        border: 'none',
                        backgroundColor: 'transparent',
                        fontFamily: 'inherit',
                    };
                    const legendContent = (
                        <>
                            <div style={{ width: '0.5rem', height: '0.5rem', backgroundColor: point.color, marginRight: '0.25rem', borderRadius: '50%', flexShrink: 0 }} />
                            <span>{point.legend}</span>
                        </>
                    );

                    return point.onClick ? (
                        <button
                            key={index}
                            type="button"
                            style={legendStyle}
                            onClick={point.onClick}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = tokens.colorNeutralBackground3;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            aria-label={`Filter by ${point.legend}`}
                            title={`Click to filter by ${point.legend}`}
                        >
                            {legendContent}
                        </button>
                    ) : (
                        <div key={index} style={legendStyle}>
                            {legendContent}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

function FilterPanel(props: FilterPanelProps) {
    const styles = useStyles();
    const [selectedKeys, setSelectedKeys] = React.useState<(string[] | number[] | null)[]>([null, null, null, null, null, null]);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [dateValidationError, setDateValidationError] = useState<string | null>(null);
    const manualInvoicesRef = React.useRef<HTMLDivElement>(null);

    // Auto-scroll to the manual invoices section when Ariba is selected
    const isAribaSelected = props.searchParams.filters.supplier?.some(s => s.toLowerCase() === 'ariba') ?? false;
    React.useEffect(() => {
        if (isAribaSelected && manualInvoicesRef.current) {
            manualInvoicesRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [isAribaSelected]);

    // Handling filter changes
    const onDropdownChange = (event: any, data: any, filterKey: FilterKey, index: number): void => {
        // v9 Dropdown onOptionSelect gives us the selected options
        // But here we are managing selection manually via props.setSearchParams
        // We need to handle multi-select logic.
        
        // Since v9 Dropdown is controlled differently, let's adapt.
        // data.selectedOptions is an array of keys.
        
        const newSelectedKeys = data.selectedOptions;
        
        // Update local state
        const updatedKeys = [...selectedKeys];
        updatedKeys[index] = newSelectedKeys.length > 0 ? newSelectedKeys : null;
        setSelectedKeys(updatedKeys);

        // Update search params
        const updatedFilters = {
            ...props.searchParams.filters,
            [filterKey]: newSelectedKeys
        };

        // Clear manual invoices checkbox when Ariba is deselected from Suppliers
        if (filterKey === 'supplier' && !newSelectedKeys.some((s: string) => s.toLowerCase() === 'ariba')) {
            updatedFilters.scenario = [];
        }

        props.setSearchParams({
            ...props.searchParams,
            filters: updatedFilters
        });
    };

    const onSearchDropdownChange = (event: any, data: any): void => {
        // data.optionValue is the key
        const isExact = data.optionValue === 'Exact Search';
        props.setExactSearch(isExact);
    };

    // Convert donut chart data to stacked bar chart data
    function getStackedBarData(list: string, aggregatedTotals: any): IChartDataPoint[] {
        return Object.entries<number>(aggregatedTotals).map(([key, value], index: number) => {
            const friendlyKey = getKeyFriendlyName(list, key);
            return {
                legend: friendlyKey,
                data: value,
                color: getColor(list, friendlyKey),
                key: key
            };
        });
    }

    // Hardcoded colors since we removed getTheme
    const _colors: { [key: string]: { [key: string]: string } } =
    {
        'status':
        {
            "In Progress": '#0078d4', // blue
            "Failed": '#a80000', // red
            "Succeeded": '#107c10', // green
            "Warning": '#6B5300', // dark gold (WCAG AA compliant)
            "Cancel": '#986F0B', // dark amber
            "Cancel Succeeded": '#0b6a0b', // greenDark
            "Cancel Failed": '#750b1c', // redDark
            "Cancel In Progress": '#004e8c', // blueDark
            "Batched": '#8764B8', // purple
            "Sent": '#4b65e1' // indigo
        },
        'mode': {
            "UAT": '#0078d4',
            "Live": '#107c10',
            "Test": '#5C4D00' // dark gold (WCAG AA compliant)
        },
        'documentType': {
            'Invoice': '#8C0076', // dark magenta (WCAG AA compliant)
            'Credit Note': '#6B4200', // dark brown (WCAG AA compliant)
            'Payment Receipt': '#0078d4', // blue
            'Debit Note': '#5C4D00' // dark gold (WCAG AA compliant)
        },
        'channel': {
            'Commercial': '#005E54', // dark teal (WCAG AA compliant)
            'Consumer': '#5c2d91', // purple
            'MilanInvoicing': '#a80000' // red
        }
    };

    // Assign color to chart elements
    function getColor(list: string, key: string): string {
        return _colors[list]?.[key] ?? '#0078d4';
    }

    const handleDataClick = (data: IChartDataPoint) => {
        props.setSearchParams({
            ...props.searchParams,
            filters: {
                ...props.searchParams.filters,
                [data.chartTitle ?? '']: [data.key]
            }
        });
    };

    // Map raw data to chart props format
    function mapToStackedBarChartData(raw: any[]): IChartProps[] {
        return raw.map(row => {
            // Convert data to chart format
            const chartPoints = getStackedBarData(row.dimension, row.aggregatedTotals);

            // Add chart title and click handlers
            const chartData = chartPoints.map(point => ({
                ...point,
                chartTitle: row.dimension,
                onClick: () => handleDataClick({
                    ...point,
                    chartTitle: row.dimension,
                    key: point.key as string
                } as IChartDataPoint)
            }));

            return {
                chartTitle: ChartTitleFriendlyNames[row.dimension] || row.dimension,
                chartData: chartData
            };
        });
    }

    function resetSearchData() {
        props.setSearchParams(getDefaultSearchParams());
        setSelectedKeys([null, null, null, null, null, null]);
    }

    function setEndTimeToNow() {
        props.setSearchParams({ ...props.searchParams, endTime: new Date() });
    }

    // Date and time conversion functions
    function convertUTCDateToLocalDate(date: Date) {
        var newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
        var offset = date.getTimezoneOffset() / 60;
        var hours = date.getHours();
        newDate.setHours(hours - offset);
        return newDate;
    }

    function endTimeChanged(e: React.ChangeEvent<HTMLInputElement>) {
        const timeString = e.target.value;
        if (!timeString) return;
        
        const [hours, minutes] = timeString.split(':').map(Number);
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);

        var newDate = props.searchParams.endTime
        if (props.useUTC) {
            var utcDate = new Date(newDate.getTime() + newDate.getTimezoneOffset() * 60000);
            utcDate.setHours(date.getHours())
            utcDate.setMinutes(date.getMinutes())
            utcDate.setSeconds(0, 0)
            newDate = convertUTCDateToLocalDate(utcDate);
        }
        else {
            newDate.setHours(date.getHours())
            newDate.setMinutes(date.getMinutes())
            newDate.setSeconds(0, 0)
        }
        const error = validateDateRange(props.searchParams.startTime, newDate);
        setDateValidationError(error);
        props.setSearchParams({ ...props.searchParams, endTime: newDate });
    }

    function validateDateRange(startTime: Date, endTime: Date): string | null {
        if (startTime >= endTime) {
            return "Start date/time must be before end date/time. Please adjust the start date to be earlier than the end date, or set the end date to a later value.";
        }
        return null;
    }

    function endDateChanged(date: Date | null | undefined) {
        var newDate = date
        if (newDate && newDate !== null) {
            if (props.useUTC) {
                newDate = convertUTCDateToLocalDate(newDate);
            }
            newDate.setHours(props.searchParams.endTime.getHours())
            newDate.setMinutes(props.searchParams.endTime.getMinutes())
            const error = validateDateRange(props.searchParams.startTime, newDate);
            setDateValidationError(error);
            props.setSearchParams({ ...props.searchParams, endTime: newDate });
        }
    }

    function startDateChanged(date: Date | null | undefined) {
        var newDate = date
        if (newDate && newDate !== null) {
            if (props.useUTC) {
                newDate = convertUTCDateToLocalDate(newDate);
            }
            newDate.setHours(props.searchParams.startTime.getHours())
            newDate.setMinutes(props.searchParams.startTime.getMinutes())
            const error = validateDateRange(newDate, props.searchParams.endTime);
            setDateValidationError(error);
            props.setSearchParams({ ...props.searchParams, startTime: newDate });
        }
    }

    function startTimeChanged(e: React.ChangeEvent<HTMLInputElement>) {
        const timeString = e.target.value;
        if (!timeString) return;
        
        const [hours, minutes] = timeString.split(':').map(Number);
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);

        var newDate = props.searchParams.startTime
        if (props.useUTC) {
            var utcDate = new Date(newDate.getTime() + newDate.getTimezoneOffset() * 60000);
            utcDate.setHours(date.getHours())
            utcDate.setMinutes(date.getMinutes())
            utcDate.setSeconds(0, 0)
            newDate = convertUTCDateToLocalDate(utcDate);
        }
        else {
            newDate.setHours(date.getHours())
            newDate.setMinutes(date.getMinutes())
            newDate.setSeconds(0, 0)
        }
        const error = validateDateRange(newDate, props.searchParams.endTime);
        setDateValidationError(error);
        props.setSearchParams({ ...props.searchParams, startTime: newDate });
    }

    // Chart dimensions
    const barChartDimensions: FilterKey[] = [
        'status',
        'documentType',
        'channel',
        'mode'
    ];

    // Filter dropdown dimensions
    const filterDropDowns: FilterKey[] = [
        'eInvPartnerId',
        'countryCode',
        'supplier',
        'errorCode',
        'salesMotion',
        'paymentType'
    ];

    function GetDateValue(useUTC: boolean, date: Date): Date {
        return useUTC ? new Date(date.getTime() + date.getTimezoneOffset() * 60000) : date;
    }

    function formatTimeForInput(date: Date): string {
        const d = GetDateValue(props.useUTC, date);
        return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }

    function formatDateForInput(date: Date): string {
        const d = GetDateValue(props.useUTC, date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const onInputDateChange = (ev: any, data: any, isStart: boolean) => {
        const dateString = data.value;
        if (!dateString) return;
        const [year, month, day] = dateString.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day);
        if (isStart) startDateChanged(dateObj);
        else endDateChanged(dateObj);
    }

    // Handle removing a filter pill
    const removeFilter = (dimension: FilterKey, value: string) => {
        props.setSearchParams({
            ...props.searchParams,
            filters: {
                ...props.searchParams.filters,
                [dimension]: props.searchParams.filters[dimension].filter((val) => val !== value)
            }
        });

        // Update the selected keys state as well
        const index = filterDropDowns.findIndex(item => item === dimension);
        if (index >= 0 && selectedKeys[index] != null) {
            const array = selectedKeys[index] as string[];
            const indexToRemove = array.indexOf(value);
            if (indexToRemove >= 0) {
                array.splice(indexToRemove, 1);
                setSelectedKeys(Object.assign([...selectedKeys], {
                    [index]: array.length > 0 ? [...array] : null
                }));
            }
        }
    };    // Generate filter pills
    const renderFilterPills = () => {
        const pills = [];

        // Time range pill - respect UTC setting
        const displayStartDate = GetDateValue(props.useUTC, props.searchParams.startTime);
        const displayEndDate = GetDateValue(props.useUTC, props.searchParams.endTime);

        const startDate = displayStartDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
        const startTime = displayStartDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const endDate = displayEndDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
        const endTime = displayEndDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        const timeZoneLabel = props.useUTC ? ' UTC' : '';

        // Handler to reset only the time range (startTime and endTime)
        const resetTimeRange = () => {
            props.setSearchParams({
                ...props.searchParams,
                startTime: getDefaultSearchParams().startTime,
                endTime: getDefaultSearchParams().endTime
            });
        };

        pills.push(
            <div key="timerange" className={styles.pill}>
                <span title={`${startDate} ${startTime} - ${endDate} ${endTime}${timeZoneLabel}`}>
                    {`${startDate} ${startTime} - ${endDate} ${endTime}${timeZoneLabel}`}
                </span>
                <Button
                    icon={<ArrowCounterclockwiseRegular fontSize={12} />}
                    title="Reset time range"
                    aria-label="Reset time range"
                    aria-description="Reset the start and end time filters to their default values"
                    onClick={resetTimeRange}
                    className={styles.pillButton}
                    appearance="transparent"
                    size="small"
                />
            </div>
        );

        // Filter dimension pills
        Object.entries(props.searchParams.filters).forEach(([dimension, values]) => {
            if (values && values.length > 0) {
                values.forEach((value: string) => {
                    // Get friendly names for both dimension and value
                    const friendlyDimension = ChartTitleFriendlyNames[dimension] || dimension;
                    const friendlyValue = getKeyFriendlyName(dimension, value);

                    pills.push(
                        <div key={`${dimension}-${value}`} className={styles.pill}>
                            <span title={`${friendlyDimension}: ${friendlyValue}`}>
                                <strong>{friendlyDimension}:</strong> {friendlyValue}
                            </span>
                            <Button
                                icon={<DismissRegular fontSize={12} />}
                                title={`Remove ${friendlyValue} filter`}
                                aria-label={`Remove ${friendlyValue} filter`}
                                aria-description={`Remove the ${friendlyDimension} filter for ${friendlyValue} from active filters`}
                                onClick={() => removeFilter(dimension as FilterKey, value)}
                                className={styles.pillButton}
                                appearance="transparent"
                                size="small"
                            />
                        </div>
                    );
                });
            }
        });

        return pills.length > 0 ? (
            <div className={styles.filterPillsContainer}>
                {pills}
            </div>
        ) : null;
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <Input
                    className={styles.searchBox}
                    placeholder="Search for document ids, partners, customer ids, etc.."
                    aria-label="Search for document ids, partners, customer ids, etc."
                    aria-description="Enter document IDs, partner names, customer IDs, or other search terms to filter results"
                    autoComplete="off"
                    contentBefore={<SearchRegular />}
                    onChange={(_ev: any, data: any) => props.setSearchParams({ ...props.searchParams, searchText: data.value })}
                />
                <Dropdown
                    onOptionSelect={onSearchDropdownChange}
                    defaultValue="Exact Search"
                    style={{ minWidth: '9.375rem' }}
                    aria-label="Search type"
                    aria-description="Choose between exact match or wildcard search mode"
                    aria-haspopup="listbox"
                >
                    <Option value="Exact Search">Exact Search</Option>
                    <Option value="WildCard Search">WildCard Search</Option>
                </Dropdown>
                <Button
                    onClick={resetSearchData}
                    icon={<ArrowCounterclockwiseRegular />}
                    aria-description="Clear all search text and filters to default values"
                >
                    Reset Search
                </Button>
            </div>

            {/* Filters Button below search bar, in front of filter pills */}
            <div className={styles.filterButtonContainer}>
                <Button
                    onClick={() => setIsPanelOpen(true)}
                    icon={<FilterRegular />}
                    aria-description="Open filter panel to refine search by date, partner, country, and more"
                >
                    Filters
                </Button>
                {/* Filter Pills */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {renderFilterPills()}
                </div>
            </div>

            {/* Bar Charts */}
            <div className={styles.chartContainer}>
                <div className={styles.chartRow} tabIndex={0} role="region" aria-label="Document status charts">
                    {mapToStackedBarChartData(barChartDimensions
                        .map(dimension => props.facetResults.find(f => f.dimension === dimension))
                        .filter(Boolean)
                    ).map((item, index) => {
                        const dimension = barChartDimensions[index];
                        return (
                            <div
                                key={`chart-${dimension}`}
                                id={`chart-${dimension}`}
                                className={styles.chartItem}
                                role="group"
                                aria-label={`Filter by ${ChartTitleFriendlyNames[dimension] || dimension}`}
                            >
                                <SimpleStackedBarChart data={item} />
                            </div>
                        );
                    })}
                </div>
            </div>

            <OverlayDrawer
                open={isPanelOpen}
                onOpenChange={(_, { open }) => setIsPanelOpen(open)}
                position="end"
                className={styles.drawer}
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                aria-description="Close the filter options panel"
                                icon={<DismissRegular />}
                                onClick={() => setIsPanelOpen(false)}
                            />
                        }
                    >
                        Filter Options
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody className={styles.drawerBody} tabIndex={0} aria-label="Filter options panel content">
                    <div className={styles.card}>
                        <div className={styles.dateTimeContainer}>
                            <Label weight="semibold">Time Range</Label>
                            {dateValidationError && (
                                <MessageBar intent="error" aria-live="assertive">
                                    <MessageBarBody>{dateValidationError}</MessageBarBody>
                                </MessageBar>
                            )}
                            {/* First row: Start Date and Time */}
                            <div className={styles.dateTimeRow}>
                                <div className={styles.datePicker}>
                                    <Label htmlFor="start-date-input">Start Date</Label>
                                    <Input
                                        id="start-date-input"
                                        type="date"
                                        className={styles.inputControl}
                                        value={formatDateForInput(props.searchParams.startTime)}
                                        onChange={(_ev: any, data: any) => onInputDateChange(_ev, data, true)}
                                        autoComplete="off"
                                        aria-label="Start date"
                                        aria-invalid={dateValidationError ? true : undefined}
                                        aria-errormessage={dateValidationError ? "date-range-error" : undefined}
                                    />
                                </div>
                                <div className={styles.timeInput}>
                                    <Label htmlFor="start-time-input">Start Time</Label>
                                    <Input
                                        id="start-time-input"
                                        type="time"
                                        className={styles.inputControl}
                                        value={formatTimeForInput(props.searchParams.startTime)}
                                        onChange={startTimeChanged}
                                        aria-label="Start time"
                                        autoComplete="off"
                                        aria-invalid={dateValidationError ? true : undefined}
                                        aria-errormessage={dateValidationError ? "date-range-error" : undefined}
                                    />
                                </div>
                            </div>

                            {/* Second row: End Date and Time with Now button */}
                            <div className={styles.dateTimeRow}>
                                <div className={styles.datePicker}>
                                    <Label htmlFor="end-date-input">End Date</Label>
                                    <Input
                                        id="end-date-input"
                                        type="date"
                                        className={styles.inputControl}
                                        value={formatDateForInput(props.searchParams.endTime)}
                                        onChange={(_ev: any, data: any) => onInputDateChange(_ev, data, false)}
                                        autoComplete="off"
                                        aria-label="End date"
                                        aria-invalid={dateValidationError ? true : undefined}
                                        aria-errormessage={dateValidationError ? "date-range-error" : undefined}
                                    />
                                </div>
                                <div className={styles.timeInput}>
                                    <Label htmlFor="end-time-input">End Time</Label>
                                    <Input
                                        id="end-time-input"
                                        type="time"
                                        className={styles.inputControl}
                                        value={formatTimeForInput(props.searchParams.endTime)}
                                        onChange={endTimeChanged}
                                        aria-label="End time"
                                        autoComplete="off"
                                        aria-invalid={dateValidationError ? true : undefined}
                                        aria-errormessage={dateValidationError ? "date-range-error" : undefined}
                                    />
                                </div>
                                <Button
                                    onClick={setEndTimeToNow}
                                    aria-label="Set end time to current time"
                                    aria-description="Set the end date and time filter to the current date and time"
                                >
                                    Now
                                </Button>
                            </div>
                            {dateValidationError && (
                                <span id="date-range-error" style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 }} role="alert">
                                    {dateValidationError}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className={styles.card}>
                        {filterDropDowns.map((dimension, index) => {
                            const facet = props.facetResults.find(f => f.dimension === dimension);
                            if (!facet) return null;

                            const options = getOptionsFromDimension(facet, true);

                            const labelId = `filter-label-${dimension}`;
                            return (
                                <div key={dimension} className={styles.dropdown}>
                                    <Label id={labelId}>{ChartTitleFriendlyNames[dimension] || dimension}</Label>
                                    <Dropdown
                                        multiselect
                                        className={styles.inputControl}
                                        placeholder="Select options..."
                                        selectedOptions={selectedKeys[index] as string[] || []}
                                        onOptionSelect={(_ev: any, data: any) => onDropdownChange(_ev, data, dimension, index)}
                                        aria-labelledby={labelId}
                                        aria-label={`Filter by ${ChartTitleFriendlyNames[dimension] || dimension}`}
                                        aria-haspopup="listbox"
                                    >
                                        {options.map((opt: any) => (
                                            <Option key={opt.key} value={opt.key} text={opt.text}>
                                                {opt.text}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>
                            );
                        })}
                    </div>

                    {Configuration.featureFlags?.manualInvoices && props.searchParams.filters.supplier?.some(s => s.toLowerCase() === 'ariba') && (
                        <div className={styles.card} ref={manualInvoicesRef} style={{ marginTop: tokens.spacingVerticalXXXL }}>
                            <Checkbox
                                checked={props.searchParams.filters.scenario?.includes('manual') ?? false}
                                onChange={(_ev: any, data: any) => {
                                    props.setSearchParams({
                                        ...props.searchParams,
                                        filters: {
                                            ...props.searchParams.filters,
                                            scenario: data.checked ? ['manual'] : []
                                        }
                                    });
                                }}
                                label="View manual invoices"
                            />
                            <Label size="small" style={{ color: tokens.colorNeutralForeground3 }}>
                                Show Ariba manual upload records (hidden by default to avoid polluting automated pipeline metrics)
                            </Label>
                        </div>
                    )}
                </DrawerBody>
            </OverlayDrawer>
        </div>
    );
}

export default FilterPanel;
